import type {
    Filters,
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    useCallback,
    useEffect,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    splClient,
} from "../api/client.ts";
import type {
    BrowsingMode,
} from "../constants.ts";
import {
    useViewerStore,
} from "../stores/viewerStore.ts";
import {
    extractErrorMessage,
} from "./utils.ts";

type SortByField =
    | "upload_date"
    | "views"
    | "votes"
    | "id"
    | "ai_analyzed_at"
    | "wilson_score"
    | "controversial"
    | "recommended";

const browsingModeToSort = (mode: BrowsingMode, filters?: Filters): {
    sortBy: SortByField;
    sortOrder: "asc" | "desc";
    tagQuery?: string;
} => {
    let sortBy: SortByField;
    let sortOrder: "asc" | "desc";
    const tagQuery = filters?.tag_query;

    switch (mode) {
        case "newest":
        case "untagged":
            sortBy = "upload_date";
            sortOrder = "desc";
            break;
        case "oldest":
            sortBy = "upload_date";
            sortOrder = "asc";
            break;
        case "ai_analyzed":
            sortBy = "ai_analyzed_at";
            sortOrder = "desc";
            break;
        case "best":
            sortBy = "wilson_score";
            sortOrder = "desc";
            break;
        case "controversial":
            sortBy = "controversial";
            sortOrder = "desc";
            break;
        case "recommended":
            sortBy = "recommended";
            sortOrder = "desc";
            break;
        default:
            sortBy = "upload_date";
            sortOrder = "desc";
    }

    return {
        sortBy,
        sortOrder,
        tagQuery,
    };
};

export const fetchOrderedMedia = (
    mode: BrowsingMode,
    filters?: Filters,
    offset = 0,
    limit = 50,
) => {
    const {
        sortBy,
        sortOrder,
        tagQuery,
    } = browsingModeToSort(mode, filters);
    // cast sortBy — API accepts wilson_score/controversial/recommended but SDK type is narrow
    return splClient.getOrderedMedia({
        sortBy: sortBy as "upload_date",
        sortOrder,
        tagQuery,
        tags: filters?.tags,
        tagMode: filters?.tag_mode,
        minViews: filters?.min_views,
        maxViews: filters?.max_views,
        minVotes: filters?.min_votes,
        maxVotes: filters?.max_votes,
        searchText: filters?.search_text,
        mediaType: filters?.media_type === "all"
            ? undefined
            : filters?.media_type,
        hasWatermark: filters?.has_watermark,
        minAiConfidence: filters?.min_ai_confidence,
        offset,
        limit,
    });
};

type UseViewerNavigationReturn = {
    loadNextItem: () => Promise<void>;
    loadPreviousItem: () => Promise<void>;
    canGoPrevious: () => boolean;
    handleNavigateToItem: (item: MediaItem) => void;
};

export const useViewerNavigation = (): UseViewerNavigationReturn => {
    const {
        currentItem,
        isLoading,
        browsingMode,
        orderedItems,
        orderedIndex,
        orderedHasMore,
        prefetchedItem,
        filters,
        recentlyViewedIds,
        searchQuery,
        searchResults,
        searchResultsIndex,
        searchHasMore,
        isSearchActive,
        setCurrentItem,
        setIsLoading,
        setError,
        setTotalFound,
        setOrderedItems,
        appendOrderedItems,
        setOrderedIndex,
        setPrefetchedItem,
        addRecentlyViewed,
        addToHistory,
        canGoBack,
        goBack,
        setSearchResultsIndex,
        setIsSearching,
        appendSearchResults,
    } = useViewerStore();

    const loadNextItem = useCallback(async () => {
        if (isSearchActive && searchResults.length > 0) {
            const newIndex = searchResultsIndex + 1;

            if (newIndex < searchResults.length) {
                const item = searchResults[newIndex];
                setCurrentItem(item);
                setSearchResultsIndex(newIndex);
                addRecentlyViewed(item.id, item);
                addToHistory(item.id);
            } else if (searchHasMore) {
                setIsSearching(true);
                try {
                    const response = await splClient.search({
                        query: searchQuery,
                        mode: "unified",
                        filters: {
                            tag_query: filters.tag_query || undefined,
                            media_type: filters.media_type,
                            min_views: filters.min_views,
                            max_views: filters.max_views,
                            min_votes: filters.min_votes,
                            max_votes: filters.max_votes,
                            has_watermark: filters.has_watermark,
                            min_ai_confidence: filters.min_ai_confidence,
                        },
                        limit: 50,
                        offset: searchResults.length,
                    });

                    if (response.items.length > 0) {
                        appendSearchResults(response.items, response.has_more);
                        const item = response.items[0];
                        setCurrentItem(item);
                        setSearchResultsIndex(newIndex);
                        addRecentlyViewed(item.id, item);
                        addToHistory(item.id);
                    } else {
                        toast("No more results");
                    }
                } catch {
                    toast.error("Failed to load more results");
                } finally {
                    setIsSearching(false);
                }
            } else {
                toast("End of search results");
            }
            return;
        }

        if (prefetchedItem) {
            setCurrentItem(prefetchedItem);
            addRecentlyViewed(prefetchedItem.id, prefetchedItem);
            addToHistory(prefetchedItem.id);
            setPrefetchedItem(null);

            if (browsingMode !== "random" && orderedIndex >= 0) {
                const newIndex = orderedIndex + 1;
                setOrderedIndex(newIndex);
            }
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            if (browsingMode === "random") {
                const filtersToSend = filters
                    ? {
                        ...filters,
                    }
                    : {};
                if (filtersToSend.media_type === "all") {
                    delete filtersToSend.media_type;
                }

                const response = await splClient.getNextImage({
                    id: currentItem?.id,
                    filters: filtersToSend,
                    exclude_ids: recentlyViewedIds,
                });

                setCurrentItem(response);
                setTotalFound(response.total_found);
                addRecentlyViewed(response.id, response);
                addToHistory(response.id);
            } else {
                if (orderedItems.length === 0) {
                    const response = await fetchOrderedMedia(
                        browsingMode,
                        filters,
                        0,
                        50,
                    );
                    if (response.items.length === 0) {
                        setError("No media found");
                        return;
                    }
                    setOrderedItems(
                        response.items,
                        response.total_count,
                        response.has_more,
                    );
                    setTotalFound(response.total_count);
                    const firstItem = response.items[0];
                    setCurrentItem(firstItem);
                    addRecentlyViewed(firstItem.id, firstItem);
                    addToHistory(firstItem.id);
                } else {
                    const newIndex = orderedIndex + 1;
                    if (newIndex < orderedItems.length) {
                        const item = orderedItems[newIndex];
                        setCurrentItem(item);
                        setOrderedIndex(newIndex);
                        addRecentlyViewed(item.id, item);
                        addToHistory(item.id);
                    } else if (orderedHasMore) {
                        const response = await fetchOrderedMedia(
                            browsingMode,
                            filters,
                            orderedItems.length,
                            50,
                        );
                        if (response.items.length > 0) {
                            appendOrderedItems(
                                response.items,
                                response.has_more,
                            );
                            const item = response.items[0];
                            setCurrentItem(item);
                            setOrderedIndex(newIndex);
                            addRecentlyViewed(item.id, item);
                            addToHistory(item.id);
                        } else {
                            toast("No more items");
                        }
                    } else {
                        toast("End of list");
                    }
                }
            }
        } catch (err) {
            const message = extractErrorMessage(err, "Failed to load media");
            setError(message);
            if (message !== "No media found") {
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [
        browsingMode,
        currentItem?.id,
        filters,
        recentlyViewedIds,
        orderedItems,
        orderedIndex,
        orderedHasMore,
        prefetchedItem,
        isSearchActive,
        searchResults,
        searchResultsIndex,
        searchHasMore,
        searchQuery,
        setCurrentItem,
        setIsLoading,
        setError,
        setTotalFound,
        setOrderedItems,
        appendOrderedItems,
        setOrderedIndex,
        setPrefetchedItem,
        addRecentlyViewed,
        addToHistory,
        setSearchResultsIndex,
        setIsSearching,
        appendSearchResults,
    ]);

    const loadPreviousItem = useCallback(async () => {
        if (isSearchActive && searchResultsIndex > 0) {
            const newIndex = searchResultsIndex - 1;
            const item = searchResults[newIndex];
            setCurrentItem(item);
            setSearchResultsIndex(newIndex);
            return;
        }

        if (browsingMode !== "random" && orderedIndex > 0) {
            const newIndex = orderedIndex - 1;
            const item = orderedItems[newIndex];
            setCurrentItem(item);
            setOrderedIndex(newIndex);
            return;
        }

        const previousId = goBack();
        if (!previousId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await splClient.getMediaItem(previousId);
            setCurrentItem(response);
        } catch (err) {
            const message = extractErrorMessage(
                err,
                "Failed to load previous item",
            );
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }, [
        browsingMode,
        orderedIndex,
        orderedItems,
        goBack,
        setCurrentItem,
        setOrderedIndex,
        setIsLoading,
        setError,
        isSearchActive,
        searchResultsIndex,
        searchResults,
        setSearchResultsIndex,
    ]);

    const canGoPrevious = useCallback(() => {
        if (isSearchActive && searchResultsIndex > 0) return true;
        if (browsingMode !== "random" && orderedIndex > 0) return true;
        return canGoBack();
    }, [
        browsingMode,
        orderedIndex,
        canGoBack,
        isSearchActive,
        searchResultsIndex,
    ]);

    const handleNavigateToItem = useCallback((item: MediaItem) => {
        setCurrentItem(item);
        addRecentlyViewed(item.id, item);
        addToHistory(item.id);

        if (browsingMode !== "random" && orderedItems.length > 0) {
            const itemIndex = orderedItems.findIndex((ordered) =>
                ordered.id === item.id
            );
            if (itemIndex >= 0) {
                setOrderedIndex(itemIndex);
            }
        }

        if (isSearchActive && searchResults.length > 0) {
            const itemIndex = searchResults.findIndex((result) =>
                result.id === item.id
            );
            if (itemIndex >= 0) {
                setSearchResultsIndex(itemIndex);
            }
        }
    }, [
        browsingMode,
        orderedItems,
        isSearchActive,
        searchResults,
        setCurrentItem,
        addRecentlyViewed,
        addToHistory,
        setOrderedIndex,
        setSearchResultsIndex,
    ]);

    // prefetch next item
    useEffect(() => {
        if (!currentItem || prefetchedItem || isLoading) return;

        const prefetch = async () => {
            try {
                if (browsingMode === "random") {
                    const filtersToSend = filters
                        ? {
                            ...filters,
                        }
                        : {};
                    if (filtersToSend.media_type === "all") {
                        delete filtersToSend.media_type;
                    }

                    const response = await splClient.getNextImage({
                        id: currentItem.id,
                        filters: filtersToSend,
                        exclude_ids: [...recentlyViewedIds, currentItem.id],
                    });
                    setPrefetchedItem(response);
                } else {
                    const nextIndex = orderedIndex + 1;
                    if (nextIndex < orderedItems.length) {
                        return;
                    } else if (orderedHasMore) {
                        const response = await fetchOrderedMedia(
                            browsingMode,
                            filters,
                            orderedItems.length,
                            50,
                        );
                        if (response.items.length > 0) {
                            appendOrderedItems(
                                response.items,
                                response.has_more,
                            );
                        }
                    }
                }
            } catch { /* ignore */ }
        };

        const timeoutId = setTimeout(prefetch, 300);
        return () => clearTimeout(timeoutId);
    }, [
        currentItem?.id,
        prefetchedItem,
        isLoading,
        browsingMode,
        filters,
        recentlyViewedIds,
        orderedIndex,
        orderedItems.length,
        orderedHasMore,
        setPrefetchedItem,
        appendOrderedItems,
    ]);

    return {
        loadNextItem,
        loadPreviousItem,
        canGoPrevious,
        handleNavigateToItem,
    };
};
